import type { ComponentType } from 'react';
import { ErrorPage } from '@zg-rentals/error-page-components';
import { rentalManagerLoginOptionsForUser } from '@zg-rentals/login-zillow';
import {
  buildMfaStepUpRetryHandler,
  useClearMfaRetryParam,
  zillowLoginRedirectHref,
} from '@zg-rentals/login-zillow/client';
import { Auth, type useAuth, useAuth as useAuthHook } from './AuthProvider';

type AuthUser = Exclude<ReturnType<typeof useAuth>['user'], null | undefined>;

const APP_NAME = 'rent-payment-web';

const ctaText = 'Return to Zillow Rental Manager';
const ctaUrl = '/rental-manager';

const onError = (errorMessage: string, errorCode: number) => (
  <ErrorPage
    errorMessage={errorMessage}
    errorDetails={`Error code: ${errorCode}`}
    action={<a href={ctaUrl}>{ctaText}</a>}
  />
);

export function getRedirectHref(user?: AuthUser | null) {
  return zillowLoginRedirectHref({
    baseURL: process.env.NEXT_PUBLIC_AUTH_BASE_URL,
    entryPoint: APP_NAME,
    forwardPrefix: process.env.NEXT_PUBLIC_AUTH_FORWARD_PREFIX,
    ...rentalManagerLoginOptionsForUser(user),
  });
}

const defaultBoundaryErrorProps = {
  onError: onError('unknown error', 500),
  onUnauthorized: () => onError('unauthorized', 401),
};

export function withRedirectBoundary<Props extends { user: AuthUser }>(
  Component: ComponentType<Props>,
  boundaryProps?: { loading?: React.ReactNode },
) {
  const Wrapped = (props: Omit<Props, 'user'>) => {
    const { user } = useAuthHook();
    useClearMfaRetryParam({ user });
    const redirectHref = getRedirectHref(user);

    const handleUnauthorized = buildMfaStepUpRetryHandler({
      appName: APP_NAME,
      buildNumber: process?.env?.BUILD_NUMBER,
      user,
      redirectHref,
      fallback: defaultBoundaryErrorProps.onUnauthorized,
      redirecting: boundaryProps?.loading ?? <>Redirecting...</>,
    });

    return (
      <Auth.Boundary
        {...defaultBoundaryErrorProps}
        {...boundaryProps}
        redirectHref={redirectHref}
        onUnauthorized={handleUnauthorized}
      >
        {(authorizedUser) => <Component {...(props as Props)} user={authorizedUser} />}
      </Auth.Boundary>
    );
  };
  Wrapped.displayName = `withRedirectBoundary(${Component.displayName ?? Component.name ?? 'Component'})`;
  return Wrapped;
}
